import React from "react";
import UseContext from "hooks/useContext";
import HoverCard from "components/tooltips/HoverCard";

const DAY_MS = 24 * 60 * 60 * 1000;

const fill = (text, values) =>
    Object.entries(values).reduce((result, [name, value]) => result.replace(`{${name}}`, value ?? ""), text);

const countLabel = (count, one, many) => fill(Number(count) === 1 ? one : many, { count });

// "il y a 12 jours", "il y a 1 mois", "il y a 2 ans": the missions counted have all started.
const formatElapsed = (isoDate, label) => {
    const time = new Date(isoDate).getTime();
    if (Number.isNaN(time)) return "";
    const days = Math.max(0, Math.floor((Date.now() - time) / DAY_MS));
    if (days === 0) return label("today", "aujourd'hui");
    let duration;
    if (days < 30) duration = countLabel(days, label("oneDay", "{count} jour"), label("manyDays", "{count} jours"));
    else if (days < 365) {
        const months = Math.floor(days / 30);
        duration = countLabel(months, label("oneMonth", "{count} mois"), label("manyMonths", "{count} mois"));
    } else {
        const years = Math.floor(days / 365);
        duration = countLabel(years, label("oneYear", "{count} an"), label("manyYears", "{count} ans"));
    }
    return fill(label("ago", "il y a {duration}"), { duration });
};

/**
 * Grey "4 missions" badge of a Jobbiz profile: the missions this temp worker did in the company's
 * group (only the company's own without a group). Its card, on hover and focus, details them by
 * company and gives the last one.
 */
const GroupMissionsBadge = ({ name, groupMissions }) => {
    const { language } = UseContext();
    const label = (key, defaultValue) =>
        language.getValue(`missionCreation.invitation.groupMissions.${key}`, defaultValue);
    const total = Number(groupMissions?.total) || 0;
    if (total <= 0) return null;

    const missionsLabel = (count) => countLabel(count, label("oneMission", "{count} mission"), label("manyMissions", "{count} missions"));
    const byCompany = Array.isArray(groupMissions.byCompany) ? groupMissions.byCompany : [];
    const lastMission = groupMissions.lastMission;
    const elapsed = lastMission?.date ? formatElapsed(lastMission.date, label) : "";

    const content = (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-0.5">
                <p className="ft-sm font-semibold text-gray-900">{name}</p>
                <p className="ft-xs text-gray-500">
                    {countLabel(
                        total,
                        label("oneInGroup", "{count} mission dans votre groupe"),
                        label("manyInGroup", "{count} missions dans votre groupe")
                    )}
                </p>
            </div>
            {byCompany.length > 0 ? (
                <ul className="flex flex-col gap-2">
                    {byCompany.map((company) => (
                        <li key={company.companyId} className="flex items-center justify-between gap-3 ft-sm">
                            <span className="min-w-0 truncate text-gray-700">{company.companyName}</span>
                            <span className="shrink-0 font-medium text-gray-900">{missionsLabel(company.count)}</span>
                        </li>
                    ))}
                </ul>
            ) : null}
            {lastMission ? (
                <div className="flex flex-col gap-0.5 border-t border-gray-100 pt-3">
                    <p className="ft-xs text-gray-500">
                        {[label("lastMission", "Dernière mission"), elapsed].filter(Boolean).join(" · ")}
                    </p>
                    {lastMission.title ? <p className="ft-sm text-gray-900">{lastMission.title}</p> : null}
                </div>
            ) : null}
        </div>
    );

    return (
        <HoverCard content={content} triggerClassName="block rounded-full">
            <span className="block whitespace-nowrap rounded-full bg-gray-100 px-2 py-1 ft-xs font-medium text-gray-600">
                {missionsLabel(total)}
            </span>
        </HoverCard>
    );
};

export default GroupMissionsBadge;
